import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Chip, Modal, PillButton } from '../ui';
import activityLog from '../services/activityLog';
import { downloadText, keyFileName, privateKeyIsEncrypted } from '../utils/sshKey';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as CheckIcon } from '../assets/icons/check.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import './SshKeyView.css';

/**
 * Vista de un item `ssh`. `data` ya esta en el formato nuevo (un item antiguo con `sshKey` se interpreta antes, en la
 * pagina: ver migrateLegacyData). La privada y la passphrase salen ocultas con mostrar y copiar; "Exportar privada" es
 * la unica via por la que la clave privada acaba en un fichero (plan 5.3.1): pide una confirmacion explicita.
 * Mostrar, copiar y exportar un secreto quedan en el registro de actividad local, con el nombre del item y nunca el valor.
 */
function SshKeyView({ item, data }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState({});
  const [copied, setCopied] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);

  const log = (kind) => activityLog.record(kind, { itemId: item?.id, itemName: item?.name, origin: 'item' });
  const { privateKey, publicKey, fingerprint, keyType, passphrase, comment } = data;
  const encrypted = privateKey ? privateKeyIsEncrypted(privateKey) : null;
  const rsaBits = /^rsa-(\d+)$/.exec(keyType || '');
  const weakRsa = !!rsaBits && Number(rsaBits[1]) < 3072;
  const noPassphrase = !!privateKey && encrypted === false;

  const copy = async (key, value, secret) => {
    if (!value) return;
    await navigator.clipboard.writeText(String(value));
    if (secret) log('copy_secret');
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? '' : c)), 1500);
  };
  const toggle = (key) => {
    if (!revealed[key]) log('reveal_secret');
    setRevealed((p) => ({ ...p, [key]: !p[key] }));
  };
  const exportPrivate = () => {
    log('export_private_key');
    downloadText(keyFileName(keyType), privateKey.endsWith('\n') ? privateKey : `${privateKey}\n`);
    setExportOpen(false);
    setUnderstood(false);
  };

  const secretRow = (key, label, value) => (
    <div className="sgc-ssh-view__row">
      <span className="sgc-ssh-view__label">{label}</span>
      <div className="sgc-ssh-view__value">
        <span className="sgc-ssh-view__text sgc-ssh-view__text--secret">
          {revealed[key] ? value : '••••••••'}
        </span>
        <button type="button" onClick={() => toggle(key)} title={t('dashboard.reveal')}>
          {revealed[key] ? <EyeOffIcon width={16} height={16} aria-hidden="true" /> : <EyeIcon width={16} height={16} aria-hidden="true" />}
        </button>
        <button type="button" onClick={() => copy(key, value, true)} title={t('dashboard.copy')}>
          {copied === key ? <CheckIcon width={16} height={16} aria-hidden="true" /> : <CopyIcon width={16} height={16} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {(weakRsa || noPassphrase) && (
        <div className="sgc-item-detail__alerts">
          {weakRsa && (
            <div className="sgc-item-detail__alert sgc-item-detail__alert--accent" role="status">
              <AlertTriangleIcon width={18} height={18} aria-hidden="true" />
              <p className="sgc-item-detail__alert-text">{t('ssh.weakRsa')}</p>
            </div>
          )}
          {noPassphrase && (
            <div className="sgc-item-detail__alert sgc-item-detail__alert--accent" role="status">
              <InfoIcon width={18} height={18} aria-hidden="true" />
              <p className="sgc-item-detail__alert-text">{t('ssh.noPassphraseNote')}</p>
            </div>
          )}
        </div>
      )}

      <Card className="sgc-ssh-view">
        {(keyType || fingerprint || comment) && (
          <div className="sgc-ssh-view__row">
            <div className="sgc-ssh-view__meta">
              {keyType && <Chip label={keyType} />}
              {comment && <span className="sgc-ssh-view__comment">{comment}</span>}
            </div>
            {fingerprint && (
              <div className="sgc-ssh-view__value">
                <span className="sgc-ssh-view__text" title={t('ssh.fingerprint')}>{fingerprint}</span>
                <button type="button" onClick={() => copy('fingerprint', fingerprint, false)} title={t('dashboard.copy')}>
                  {copied === 'fingerprint' ? <CheckIcon width={16} height={16} aria-hidden="true" /> : <CopyIcon width={16} height={16} aria-hidden="true" />}
                </button>
              </div>
            )}
          </div>
        )}

        {publicKey && (
          <div className="sgc-ssh-view__row">
            <span className="sgc-ssh-view__label">{t('ssh.publicKey')}</span>
            <p className="sgc-ssh-view__block">{publicKey}</p>
            <div className="sgc-ssh-view__buttons">
              <PillButton variant="secondary" size="sm" icon={copied === 'public' ? CheckIcon : CopyIcon} onClick={() => copy('public', publicKey, false)}>
                {copied === 'public' ? t('ssh.copied') : t('ssh.copyPublic')}
              </PillButton>
              <PillButton variant="secondary" size="sm" icon={DownloadIcon} onClick={() => downloadText(keyFileName(keyType, true), `${publicKey}\n`)}>
                {t('ssh.downloadPublic')}
              </PillButton>
            </div>
          </div>
        )}

        {privateKey && secretRow('privateKey', t('ssh.privateKey'), privateKey)}
        {privateKey && (
          <div className="sgc-ssh-view__buttons">
            <PillButton variant="secondary" size="sm" icon={DownloadIcon} onClick={() => setExportOpen(true)}>{t('ssh.exportPrivate')}</PillButton>
          </div>
        )}
        {passphrase && secretRow('passphrase', t('ssh.passphrase'), passphrase)}
      </Card>

      <Modal open={exportOpen} onClose={() => { setExportOpen(false); setUnderstood(false); }} title={t('ssh.exportTitle')}>
        <p>{t('ssh.exportBody')}</p>
        <p className="sgc-ssh-view__export-note">{encrypted ? t('ssh.exportProtected') : t('ssh.exportUnprotected')}</p>
        <label className="sgc-ssh-view__understood">
          <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
          {t('ssh.exportConfirm')}
        </label>
        <div className="sgc-item-detail__confirm-actions">
          <PillButton variant="secondary" onClick={() => { setExportOpen(false); setUnderstood(false); }}>{t('common.cancel')}</PillButton>
          <PillButton variant="danger" disabled={!understood} onClick={exportPrivate}>{t('ssh.exportAction')}</PillButton>
        </div>
      </Modal>
    </>
  );
}

export default SshKeyView;
